import { CircularProgress, Container } from "@mui/material";
import { AxiosResponse } from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { serviceFetchBlogsByTitle } from "src/api/service/services";

interface EmbeddedHTMLContentProps {
  title?: string;
  content?: string;
}

const EmbeddedHTMLContent: React.FC<EmbeddedHTMLContentProps> = ({
  title,
  content,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState("0px");
  const [loading, setLoading] = useState(true);
  const [apiBlog, setApiBlog] = useState<{
    title: string;
    content: string;
  } | null>(null);

  const location = useLocation();
  const state = useMemo(() => location.state || {}, [location.state]);
  const navigate = useNavigate();

  // ✅ Extract title from URL if state is empty
  useEffect(() => {
        console.log("IN EMBEED")

    if (!title && !content && !state?.title && !state?.content) {
      const pathname = location.pathname;

      // Case 1: If exact /analytics → redirect
      if (pathname === "/analytics") {
        navigate("/analytics"); // or window.location.href = "/analytics";
        return;
      }
// Case 2: If exact /blogs (no slug) → redirect to blogs section
    if (pathname === "/blogs" || pathname === "/blogs/") {
      navigate("/home#blogs-section")
      return;
    }
      // Case 3: If /blogs/<slug>
      if (pathname.startsWith("/blogs/")) {
        const slug = pathname.split("/").pop();

        if (slug) {
          serviceFetchBlogsByTitle(slug)
            .then((response: AxiosResponse) => {
              const blogData = response.data?.[0];

              if (blogData) {
                setApiBlog({
                  title: blogData?.title?.rendered || blogData?.title || "",
                  content:
                    blogData?.content?.rendered || blogData?.content || "",
                });
              } else {
                // ✅ Fallback redirect if no blog found
               navigate("/home#blogs-section")       
              }
            })
            .catch((err) => {
              console.error("Error fetching blog by title:", err);
              // Optional: also fallback on error
              navigate("/home#blogs-section")
            });
        }
      }else{
        navigate("/404")
      }
    }
  }, [title, content, state, location, navigate]);

  const finalTitle = title || state?.title || apiBlog?.title || "";
  const finalContent = content || state?.content || apiBlog?.content || "";

  const onLoad = () => {
    setHeight(
      iframeRef?.current?.contentWindow?.document.body.scrollHeight + "px"
    );
    setLoading(false);
  };

  useEffect(() => {
    const iframe = document.querySelector("iframe");
    const iframeCurrentRef = iframeRef.current;

    if (iframe && iframeCurrentRef && finalContent) {
      const doc = iframe.contentWindow?.document;

      if (doc) {
        setHeight("0px");
        setLoading(true);

        doc.open();
        doc.write(finalContent);

        // Append stylesheet links
        const head = doc.querySelector("head");
        if (head) {
          const stylesheets = [
            "/Css-WP/wp-block-image-style.css",
            "/Css-WP/font-styles.css",
            "/Css-WP/wp-block-library.css",
            "/Css-WP/wp-block-navigation.css",
            "/Css-WP/inline-styles.css",
          ];
          stylesheets.forEach((stylesheet) => {
            const link = doc.createElement("link");
            link.rel = "stylesheet";
            link.href = stylesheet;
            head.appendChild(link);
          });
        }

        // Apply styles
        doc.body.style.overflowY = "auto";
        doc.body.style.textAlign = "justify";
        doc.body.style.backgroundColor = "transparent";
        doc.body.style.paddingLeft = "0";
        doc.body.style.paddingRight = "0";
        doc.body.className =
          "entry-content alignfull wp-block-post-content has-global-padding is-layout-constrained wp-block-post-content-is-layout-constrained";

        // External link handling
        doc.addEventListener("click", (e) => {
          const a = (e.target as HTMLElement).closest(
            'a[target="_blank"]'
          ) as HTMLAnchorElement | null;
          if (a) {
            e.preventDefault();
            window.open(a.href, "_blank", "noopener,noreferrer");
          }
        });

        doc.close();
      }
    }
  }, [finalContent, apiBlog, state]);

  return (
    <Container maxWidth={"xl"}>
      {loading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </div>
      )}
      <iframe
        title={finalTitle}
        ref={iframeRef}
        scrolling="no"
        onLoad={onLoad}
        height={height}
        style={{
          width: "100%",
          border: "none",
          overflow: "hidden",
          backgroundColor: "transparent",
          minHeight: `${window.innerHeight - 250}px`,
          paddingLeft: "0",
          paddingRight: "0",
        }}
        //  sandbox="allow-same-origin allow-scripts"
      />
      <br />
    </Container>
  );
};

export default EmbeddedHTMLContent;
